/**
 * Queen Lingerie - Custom Wishlist (v2)
 * ------------------------------------------------------------
 * Fully self-contained, localStorage-based wishlist with
 * categorizable lists, per-item size/variant tracking, custom
 * social share buttons and size-aware "Add to cart".
 *
 * No dependency on any third-party wishlist/compare app.
 *
 * Markup contract:
 *   - [data-queen-wishlist-toggle][data-handle]  -> add/remove heart button
 *       optional: data-product-id, data-variant-id, data-variant-select
 *       (data-variant-select points to the id of a <select name="id">
 *        element used to read the currently selected variant)
 *   - [data-queen-wishlist-show]                  -> link to the wishlist page
 *       (contains a .queen-wishlist-count badge)
 *
 * Wishlist page contract (templates/page.arn_content_wishlist.liquid):
 *   - #queen-wishlist-page                 root element
 *   - #queen-wishlist-lists-nav            list/category tabs
 *   - #queen-wishlist-create-list-form     create-new-list form
 *   - #queen-wishlist-new-list-name        text input inside that form
 *   - #queen-wishlist-items                grid of item cards for active list
 */
(function () {
  'use strict';

  var STORAGE_KEY = 'queen_wishlist_v2';
  var LEGACY_KEY = 'queen_wishlist_v1';
  var DEFAULT_LIST_ID = 'default';

  var i18n = window.queenWishlistI18n || {};
  i18n = {
    title: i18n.title || 'Wishlist',
    empty: i18n.empty || 'Your wishlist is empty.',
    remove: i18n.remove || 'Remove',
    view_product: i18n.view_product || 'View product',
    add_to_wishlist: i18n.add_to_wishlist || 'Add to Wishlist',
    remove_from_wishlist: i18n.remove_from_wishlist || 'Remove from Wishlist',
    default_list_name: i18n.default_list_name || 'My Wishlist',
    new_list_placeholder: i18n.new_list_placeholder || 'New list name (e.g. Summer)',
    create_list: i18n.create_list || 'Add list',
    rename_list: i18n.rename_list || 'Rename list',
    rename_list_prompt: i18n.rename_list_prompt || 'Rename list',
    delete_list: i18n.delete_list || 'Delete list',
    delete_list_confirm: i18n.delete_list_confirm || 'Delete this list? Items will move to your default list.',
    move_to_list: i18n.move_to_list || 'Move to',
    size_label: i18n.size_label || 'Size',
    add_to_cart: i18n.add_to_cart || 'Add to cart',
    adding: i18n.adding || 'Adding…',
    added_to_cart: i18n.added_to_cart || 'Added!',
    unavailable: i18n.unavailable || 'Unavailable in this size',
    choose_size: i18n.choose_size || 'Select size',
    share: i18n.share || 'Share',
    link_copied: i18n.link_copied || 'Link copied'
  };

  /* ------------------------------------------------------------ */
  /* Storage / data model                                          */
  /* ------------------------------------------------------------ */

  function uid() {
    return 'i' + Date.now().toString(36) + Math.random().toString(36).slice(2, 8);
  }

  function loadData() {
    try {
      var raw = window.localStorage.getItem(STORAGE_KEY);
      if (raw) {
        var parsed = JSON.parse(raw);
        if (parsed && Array.isArray(parsed.lists) && Array.isArray(parsed.items)) {
          if (!parsed.lists.some(function (l) { return l.id === DEFAULT_LIST_ID; })) {
            parsed.lists.unshift({ id: DEFAULT_LIST_ID, name: i18n.default_list_name });
          }
          return parsed;
        }
      }
    } catch (e) {
      /* fall through to fresh/migrated data */
    }

    // Migrate from v1 (flat array of handles) if present.
    var items = [];
    try {
      var legacyRaw = window.localStorage.getItem(LEGACY_KEY);
      if (legacyRaw) {
        var legacy = JSON.parse(legacyRaw);
        if (Array.isArray(legacy)) {
          legacy.forEach(function (handle) {
            if (typeof handle === 'string' && handle) {
              items.push({
                id: uid(),
                handle: handle,
                productId: null,
                variantId: null,
                listId: DEFAULT_LIST_ID,
                addedAt: Date.now()
              });
            }
          });
        }
      }
    } catch (e) {
      /* ignore */
    }

    return {
      lists: [{ id: DEFAULT_LIST_ID, name: i18n.default_list_name }],
      items: items
    };
  }

  var data = loadData();

  function saveData() {
    try {
      window.localStorage.setItem(STORAGE_KEY, JSON.stringify(data));
      window.localStorage.removeItem(LEGACY_KEY);
    } catch (e) {
      /* localStorage unavailable - fail silently */
    }
  }

  saveData();

  function isWishlisted(handle) {
    return data.items.some(function (i) { return i.handle === handle; });
  }

  function addItem(handle, productId, variantId, listId) {
    data.items.push({
      id: uid(),
      handle: handle,
      productId: productId || null,
      variantId: variantId || null,
      listId: listId || DEFAULT_LIST_ID,
      addedAt: Date.now()
    });
    saveData();
  }

  function removeByHandle(handle) {
    data.items = data.items.filter(function (i) { return i.handle !== handle; });
    saveData();
  }

  function removeItem(itemId) {
    data.items = data.items.filter(function (i) { return i.id !== itemId; });
    saveData();
  }

  function moveItem(itemId, listId) {
    var item = data.items.filter(function (i) { return i.id === itemId; })[0];
    if (item && data.lists.some(function (l) { return l.id === listId; })) {
      item.listId = listId;
      saveData();
    }
  }

  function toggleWishlist(handle, productId, variantId) {
    if (isWishlisted(handle)) {
      removeByHandle(handle);
      return false;
    }
    addItem(handle, productId, variantId, DEFAULT_LIST_ID);
    return true;
  }

  function createList(name) {
    name = (name || '').trim();
    if (!name) {
      return null;
    }
    var list = { id: uid(), name: name };
    data.lists.push(list);
    saveData();
    return list;
  }

  function renameList(listId, name) {
    name = (name || '').trim();
    if (!name) {
      return;
    }
    var list = data.lists.filter(function (l) { return l.id === listId; })[0];
    if (list) {
      list.name = name;
      saveData();
    }
  }

  function deleteList(listId) {
    if (listId === DEFAULT_LIST_ID) {
      return;
    }
    data.lists = data.lists.filter(function (l) { return l.id !== listId; });
    data.items.forEach(function (item) {
      if (item.listId === listId) {
        item.listId = DEFAULT_LIST_ID;
      }
    });
    saveData();
  }

  /* ------------------------------------------------------------ */
  /* Money formatting (standard Shopify money_format helper)       */
  /* ------------------------------------------------------------ */

  function formatWithDelimiters(number, precision, thousands, decimal) {
    precision = precision == null ? 2 : precision;
    thousands = thousands == null ? ',' : thousands;
    decimal = decimal == null ? '.' : decimal;

    if (isNaN(number) || number == null) {
      return 0;
    }

    number = (number / 100.0).toFixed(precision);

    var parts = number.split('.');
    var dollars = parts[0].replace(/(\d)(?=(\d\d\d)+(?!\d))/g, '$1' + thousands);
    var cents = parts[1] ? decimal + parts[1] : '';

    return dollars + cents;
  }

  function formatMoney(cents) {
    var format = (window.theme && window.theme.moneyFormat) || '${{amount}}';
    var placeholderRegex = /\{\{\s*(\w+)\s*\}\}/;
    var match = format.match(placeholderRegex);
    var value = '';

    if (!match) {
      return format;
    }

    switch (match[1]) {
      case 'amount':
        value = formatWithDelimiters(cents, 2);
        break;
      case 'amount_no_decimals':
        value = formatWithDelimiters(cents, 0);
        break;
      case 'amount_with_comma_separator':
        value = formatWithDelimiters(cents, 2, '.', ',');
        break;
      case 'amount_no_decimals_with_comma_separator':
        value = formatWithDelimiters(cents, 0, '.', ',');
        break;
      default:
        value = formatWithDelimiters(cents, 2);
    }

    return format.replace(placeholderRegex, value);
  }

  /* ------------------------------------------------------------ */
  /* Icons                                                          */
  /* ------------------------------------------------------------ */

  var ICONS = {
    remove: '<svg viewBox="0 0 24 24" width="14" height="14" aria-hidden="true"><path fill="currentColor" d="M18.3 5.71a1 1 0 0 0-1.42 0L12 10.59 7.11 5.7A1 1 0 0 0 5.7 7.11L10.59 12 5.7 16.89a1 1 0 1 0 1.41 1.41L12 13.41l4.89 4.89a1 1 0 0 0 1.41-1.41L13.41 12l4.89-4.89a1 1 0 0 0 0-1.4z"/></svg>',
    bin: '<svg viewBox="0 0 24 24" width="16" height="16" aria-hidden="true"><path fill="currentColor" d="M9 3a1 1 0 0 0-1 1v1H4v2h1v12a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V7h1V5h-4V4a1 1 0 0 0-1-1H9zm0 2h6v0H9v0zM7 7h10v12H7V7zm2 2v8h2V9H9zm4 0v8h2V9h-2z"/></svg>',
    facebook: '<svg viewBox="0 0 24 24" width="16" height="16" aria-hidden="true"><path fill="currentColor" d="M18.77 7.46H14.5V5.56c0-.9.59-1.11 1.01-1.11h2.95V.51L14.17.5C10.24.5 9.5 3.44 9.5 5.32v2.14h-3v4h3V20h5v-8.54h3.85l.42-4z"/></svg>',
    twitter: '<svg viewBox="0 0 24 24" width="16" height="16" aria-hidden="true"><path fill="currentColor" d="M23.44 4.83c-.81.36-1.5.38-2.22.02.94-.56.98-.96 1.32-2.02-.88.52-1.85.9-2.89 1.1-.83-.88-2.01-1.44-3.32-1.44-2.51 0-4.55 2.04-4.55 4.54 0 .36.04.7.12 1.03-3.78-.19-7.13-2-9.37-4.75-.39.67-.62 1.45-.62 2.28 0 1.58.8 2.97 2.02 3.78-.74-.02-1.44-.23-2.05-.57v.06c0 2.2 1.57 4.04 3.65 4.46-.38.1-.79.16-1.21.16-.3 0-.58-.03-.86-.08.58 1.8 2.26 3.12 4.25 3.15-1.56 1.22-3.52 1.95-5.65 1.95-.37 0-.73-.02-1.08-.06 2.01 1.29 4.4 2.04 6.97 2.04 8.36 0 12.93-6.92 12.93-12.93 0-.2 0-.39-.01-.59.89-.63 1.66-1.45 2.27-2.36z"/></svg>',
    pinterest: '<svg viewBox="0 0 24 24" width="16" height="16" aria-hidden="true"><path fill="currentColor" d="M12.14.5C5.86.5 2.7 5 2.7 8.75c0 2.27.8 4.3 2.7 5.05.3.12.57 0 .66-.33.06-.23.2-.82.27-1.06.09-.33.05-.45-.19-.74-.53-.62-.87-1.44-.87-2.59 0-3.34 2.5-6.33 6.5-6.33 3.55 0 5.5 2.17 5.5 5.07 0 3.81-1.69 7.02-4.19 7.02-1.38 0-2.42-1.14-2.08-2.55.4-1.67 1.17-3.48 1.17-4.69 0-1.08-.58-1.98-1.78-1.98-1.41 0-2.55 1.46-2.55 3.42 0 1.25.42 2.09.42 2.09s-1.45 6.13-1.7 7.2c-.5 2.14-.08 4.76-.04 5.02.02.16.22.2.31.08.13-.17 1.81-2.25 2.39-4.33.16-.59.93-3.63.93-3.63.46.88 1.8 1.65 3.23 1.65 4.25 0 7.13-3.87 7.13-9.05C20.5 4.15 17.18.5 12.14.5z"/></svg>',
    email: '<svg viewBox="0 0 24 24" width="16" height="16" aria-hidden="true"><path fill="currentColor" d="M22 4H2C.9 4 0 4.9 0 6v12c0 1.1.9 2 2 2h20c1.1 0 2-.9 2-2V6c0-1.1-.9-2-2-2zm0 4.24-10 6.25L2 8.24V6l10 6.25L22 6v2.24z"/></svg>',
    link: '<svg viewBox="0 0 24 24" width="16" height="16" aria-hidden="true"><path fill="currentColor" d="M3.9 12c0-1.71 1.39-3.1 3.1-3.1h4V7H7a5 5 0 1 0 0 10h4v-1.9H7A3.1 3.1 0 0 1 3.9 12zM8 13h8v-2H8v2zm9-7h-4v1.9h4a3.1 3.1 0 1 1 0 6.2h-4V16h4a5 5 0 0 0 0-10z"/></svg>',
    pencil: '<svg viewBox="0 0 24 24" width="13" height="13" aria-hidden="true"><path fill="currentColor" d="M3 17.25V21h3.75L17.81 9.94l-3.75-3.75L3 17.25zM20.71 7.04a1 1 0 0 0 0-1.41l-2.34-2.34a1 1 0 0 0-1.41 0l-1.83 1.83 3.75 3.75 1.83-1.83z"/></svg>'
  };

  /* ------------------------------------------------------------ */
  /* Button states + count badge                                   */
  /* ------------------------------------------------------------ */

  function refreshButtonStates() {
    var buttons = document.querySelectorAll('[data-queen-wishlist-toggle][data-handle]');
    for (var i = 0; i < buttons.length; i++) {
      setToggleState(buttons[i], isWishlisted(buttons[i].getAttribute('data-handle')));
    }
  }

  function setToggleState(btn, active) {
    btn.classList.toggle('is-wishlisted', active);
    var label = btn.querySelector('span');
    if (label) {
      label.textContent = active ? i18n.remove_from_wishlist : i18n.add_to_wishlist;
    }
    btn.setAttribute('title', active ? i18n.remove_from_wishlist : i18n.add_to_wishlist);
    btn.setAttribute('aria-pressed', active ? 'true' : 'false');
  }

  function updateCount() {
    var count = data.items.length;
    var badges = document.querySelectorAll('.queen-wishlist-count');
    for (var i = 0; i < badges.length; i++) {
      badges[i].textContent = count;
      badges[i].classList.toggle('is-visible', count > 0);
    }
  }

  function getVariantIdForButton(btn) {
    var selectId = btn.getAttribute('data-variant-select');
    if (selectId) {
      var select = document.getElementById(selectId);
      if (select && select.value) {
        return select.value;
      }
    }
    return btn.getAttribute('data-variant-id') || null;
  }

  /* ------------------------------------------------------------ */
  /* Product data fetching                                         */
  /* ------------------------------------------------------------ */

  var productCache = {};

  function fetchProduct(handle) {
    if (productCache[handle]) {
      return productCache[handle];
    }

    productCache[handle] = fetch('/products/' + encodeURIComponent(handle) + '.js', {
      credentials: 'same-origin'
    })
      .then(function (response) {
        if (!response.ok) {
          return null;
        }
        return response.json();
      })
      .catch(function () {
        return null;
      });

    return productCache[handle];
  }

  function escapeHtml(str) {
    if (str == null) {
      return '';
    }
    return String(str).replace(/[&<>"']/g, function (c) {
      return (
        {
          '&': '&amp;',
          '<': '&lt;',
          '>': '&gt;',
          '"': '&quot;',
          "'": '&#39;'
        }[c] || c
      );
    });
  }

  /* ------------------------------------------------------------ */
  /* Wishlist page rendering                                        */
  /* ------------------------------------------------------------ */

  var activeListId = DEFAULT_LIST_ID;

  function renderWishlistPage() {
    var root = document.getElementById('queen-wishlist-page');
    if (!root) {
      return;
    }

    if (!data.lists.some(function (l) { return l.id === activeListId; })) {
      activeListId = DEFAULT_LIST_ID;
    }

    renderListTabs();
    renderItems();
  }

  function renderListTabs() {
    var nav = document.getElementById('queen-wishlist-lists-nav');
    if (!nav) {
      return;
    }

    var html = data.lists.map(function (list) {
      var count = data.items.filter(function (i) { return i.listId === list.id; }).length;
      var active = list.id === activeListId;
      var tab =
        '<div class="queen-wishlist-tab-wrap' + (active ? ' is-active' : '') + '">' +
          '<button type="button" class="queen-wishlist-tab" data-queen-wishlist-tab data-list-id="' + escapeHtml(list.id) + '">' +
            escapeHtml(list.name) + ' <span class="queen-wishlist-tab__count">' + count + '</span>' +
          '</button>';

      if (list.id !== DEFAULT_LIST_ID) {
        tab +=
          '<button type="button" class="queen-wishlist-tab__action" data-queen-wishlist-rename-list data-list-id="' + escapeHtml(list.id) + '" title="' + escapeHtml(i18n.rename_list) + '">' + ICONS.pencil + '</button>' +
          '<button type="button" class="queen-wishlist-tab__action" data-queen-wishlist-delete-list data-list-id="' + escapeHtml(list.id) + '" title="' + escapeHtml(i18n.delete_list) + '">' + ICONS.remove + '</button>';
      }

      tab += '</div>';
      return tab;
    }).join('');

    nav.innerHTML = html;
  }

  function buildListOptions(currentListId) {
    return data.lists.map(function (l) {
      return '<option value="' + escapeHtml(l.id) + '"' + (l.id === currentListId ? ' selected' : '') + '>' + escapeHtml(l.name) + '</option>';
    }).join('');
  }

  function buildItemMarkup(item, product) {
    if (!product) {
      return '';
    }

    function toImageUrl(src) {
      if (!src) {
        return '';
      }
      if (src.indexOf('//') === 0) {
        src = 'https:' + src;
      }
      return src + (src.indexOf('?') !== -1 ? '&width=600' : '?width=600');
    }

    var images = product.images || [];
    var primaryImage = product.featured_image || images[0] || '';
    var secondaryImage = '';
    for (var imgIdx = 0; imgIdx < images.length; imgIdx++) {
      if (images[imgIdx] && images[imgIdx] !== primaryImage) {
        secondaryImage = images[imgIdx];
        break;
      }
    }

    var imageUrl = toImageUrl(primaryImage);
    var imageUrlBack = toImageUrl(secondaryImage);

    var variant = null;
    if (item.variantId) {
      variant = (product.variants || []).filter(function (v) {
        return String(v.id) === String(item.variantId);
      })[0];
    }

    var price = variant ? variant.price : product.price;
    var comparePrice = variant ? variant.compare_at_price : product.compare_at_price;
    var priceHtml = '';
    if (comparePrice && comparePrice > price) {
      priceHtml +=
        '<span class="queen-wishlist-item__price queen-wishlist-item__price--sale">' + formatMoney(price) + '</span>' +
        '<span class="queen-wishlist-item__price queen-wishlist-item__price--compare">' + formatMoney(comparePrice) + '</span>';
    } else {
      priceHtml += '<span class="queen-wishlist-item__price">' + formatMoney(price) + '</span>';
    }

    var sizeHtml = '';
    if (variant && variant.title && variant.title !== 'Default Title') {
      sizeHtml = '<div class="queen-wishlist-item__size">' + escapeHtml(i18n.size_label) + ': ' + escapeHtml(variant.title) + '</div>';
    }

    var actionHtml;
    if (item.variantId) {
      if (variant && variant.available) {
        actionHtml = '<button type="button" class="queen-wishlist-item__add-cart" data-queen-wishlist-add-cart data-item-id="' + escapeHtml(item.id) + '" data-variant-id="' + escapeHtml(String(variant.id)) + '">' + escapeHtml(i18n.add_to_cart) + '</button>';
      } else {
        actionHtml = '<button type="button" class="queen-wishlist-item__add-cart is-disabled" disabled>' + escapeHtml(i18n.unavailable) + '</button>';
      }
    } else {
      actionHtml = '<a href="' + product.url + '" class="queen-wishlist-item__add-cart queen-wishlist-item__add-cart--link">' + escapeHtml(i18n.choose_size) + '</a>';
    }

    var absoluteUrl = window.location.origin + product.url;

    var shareHtml =
      '<div class="queen-wishlist-item__share" data-share-url="' + escapeHtml(absoluteUrl) + '" data-share-title="' + escapeHtml(product.title) + '">' +
        '<span class="queen-wishlist-item__share-label">' + escapeHtml(i18n.share) + ':</span>' +
        '<button type="button" class="queen-wishlist-share-btn" data-queen-share="facebook" title="Facebook">' + ICONS.facebook + '</button>' +
        '<button type="button" class="queen-wishlist-share-btn" data-queen-share="twitter" title="Twitter / X">' + ICONS.twitter + '</button>' +
        '<button type="button" class="queen-wishlist-share-btn" data-queen-share="pinterest" title="Pinterest">' + ICONS.pinterest + '</button>' +
        '<button type="button" class="queen-wishlist-share-btn" data-queen-share="email" title="Email">' + ICONS.email + '</button>' +
        '<button type="button" class="queen-wishlist-share-btn" data-queen-share="copy" title="' + escapeHtml(i18n.share) + '">' + ICONS.link + '</button>' +
      '</div>';

    return (
      '<div class="queen-wishlist-item" data-item-id="' + escapeHtml(item.id) + '">' +
        '<div class="queen-wishlist-item__image">' +
          '<a href="' + product.url + '" class="queen-wishlist-item__image-link">' +
            (imageUrl ? '<img class="queen-wishlist-item__img queen-wishlist-item__img--front" src="' + imageUrl + '" alt="' + escapeHtml(product.title) + '" loading="lazy">' : '') +
            (imageUrlBack ? '<img class="queen-wishlist-item__img queen-wishlist-item__img--back" src="' + imageUrlBack + '" alt="" loading="lazy">' : '') +
          '</a>' +
          '<button type="button" class="queen-wishlist-item__remove" data-queen-wishlist-remove data-item-id="' + escapeHtml(item.id) + '" aria-label="' + escapeHtml(i18n.remove) + '" title="' + escapeHtml(i18n.remove) + '">' + ICONS.bin + '</button>' +
        '</div>' +
        '<div class="queen-wishlist-item__info">' +
          '<a href="' + product.url + '" class="queen-wishlist-item__title">' + escapeHtml(product.title) + '</a>' +
          sizeHtml +
          '<div class="queen-wishlist-item__price-wrap">' + priceHtml + '</div>' +
          '<div class="queen-wishlist-item__actions">' +
            actionHtml +
          '</div>' +
          (data.lists.length > 1
            ? '<label class="queen-wishlist-item__move-label">' + escapeHtml(i18n.move_to_list) +
              ': <select class="queen-wishlist-item__move" data-queen-wishlist-move data-item-id="' + escapeHtml(item.id) + '">' + buildListOptions(item.listId) + '</select></label>'
            : '') +
          shareHtml +
        '</div>' +
      '</div>'
    );
  }

  function renderItems() {
    var container = document.getElementById('queen-wishlist-items');
    if (!container) {
      return;
    }

    var items = data.items.filter(function (i) { return i.listId === activeListId; });

    if (!items.length) {
      container.innerHTML = '<p class="queen-wishlist-empty">' + escapeHtml(i18n.empty) + '</p>';
      container.classList.add('is-empty');
      return;
    }

    container.classList.remove('is-empty');
    container.innerHTML = '<p class="queen-wishlist-loading"></p>';

    Promise.all(
      items.map(function (item) {
        return fetchProduct(item.handle).then(function (product) {
          return { item: item, product: product };
        });
      })
    ).then(function (results) {
      var html = '';
      var validIds = {};
      var removedAny = false;

      results.forEach(function (result) {
        if (result.product) {
          html += buildItemMarkup(result.item, result.product);
          validIds[result.item.id] = true;
        } else {
          removedAny = true;
        }
      });

      if (removedAny) {
        data.items = data.items.filter(function (i) {
          return i.listId !== activeListId || validIds[i.id];
        });
        saveData();
        updateCount();
        renderListTabs();
      }

      if (!html) {
        container.innerHTML = '<p class="queen-wishlist-empty">' + escapeHtml(i18n.empty) + '</p>';
        container.classList.add('is-empty');
        return;
      }

      container.innerHTML = html;
    });
  }

  /* ------------------------------------------------------------ */
  /* Sharing                                                        */
  /* ------------------------------------------------------------ */

  function openPopup(url) {
    window.open(url, '_blank', 'width=600,height=420,noopener,noreferrer');
  }

  function fallbackCopy(text) {
    var ta = document.createElement('textarea');
    ta.value = text;
    ta.setAttribute('readonly', '');
    ta.style.position = 'fixed';
    ta.style.opacity = '0';
    document.body.appendChild(ta);
    ta.select();
    try {
      document.execCommand('copy');
    } catch (e) {
      /* ignore */
    }
    document.body.removeChild(ta);
  }

  function copyToClipboard(text, btn) {
    function done() {
      if (!btn) {
        return;
      }
      btn.classList.add('is-copied');
      var original = btn.getAttribute('title');
      btn.setAttribute('title', i18n.link_copied);
      setTimeout(function () {
        btn.classList.remove('is-copied');
        if (original) {
          btn.setAttribute('title', original);
        }
      }, 1500);
    }

    if (navigator.clipboard && navigator.clipboard.writeText) {
      navigator.clipboard.writeText(text).then(done, function () {
        fallbackCopy(text);
        done();
      });
    } else {
      fallbackCopy(text);
      done();
    }
  }

  function shareProduct(service, url, title, btn) {
    switch (service) {
      case 'facebook':
        openPopup('https://www.facebook.com/sharer/sharer.php?u=' + encodeURIComponent(url));
        break;
      case 'twitter':
        openPopup('https://twitter.com/intent/tweet?url=' + encodeURIComponent(url) + '&text=' + encodeURIComponent(title));
        break;
      case 'pinterest':
        openPopup('https://pinterest.com/pin/create/button/?url=' + encodeURIComponent(url) + '&description=' + encodeURIComponent(title));
        break;
      case 'email':
        window.location.href = 'mailto:?subject=' + encodeURIComponent(title) + '&body=' + encodeURIComponent(url);
        break;
      case 'copy':
        copyToClipboard(url, btn);
        break;
    }
  }

  /* ------------------------------------------------------------ */
  /* Add to cart                                                    */
  /* ------------------------------------------------------------ */

  function refreshCartIndicators(cart) {
    if (!cart) {
      return;
    }
    var selectors = ['.cart-count', '[data-cart-count]', '.js-cart-count'];
    selectors.forEach(function (selector) {
      var nodes = document.querySelectorAll(selector);
      for (var i = 0; i < nodes.length; i++) {
        nodes[i].textContent = cart.item_count;
      }
    });
    document.dispatchEvent(new CustomEvent('queen:cart-updated', { detail: { cart: cart } }));
  }

  function addToCart(variantId, btn) {
    var originalText = btn.textContent;
    btn.disabled = true;
    btn.textContent = i18n.adding;

    fetch('/cart/add.js', {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ id: variantId, quantity: 1 })
    })
      .then(function (response) {
        return response.json().then(function (json) {
          return { ok: response.ok, json: json };
        });
      })
      .then(function (result) {
        if (result.ok) {
          btn.textContent = i18n.added_to_cart;
          fetch('/cart.js', { credentials: 'same-origin' })
            .then(function (r) { return r.json(); })
            .then(refreshCartIndicators)
            .catch(function () {});
        } else {
          btn.textContent = i18n.unavailable;
        }
        setTimeout(function () {
          btn.disabled = false;
          btn.textContent = originalText;
        }, 1800);
      })
      .catch(function () {
        btn.disabled = false;
        btn.textContent = originalText;
      });
  }

  /* ------------------------------------------------------------ */
  /* Event binding                                                  */
  /* ------------------------------------------------------------ */

  function closest(el, selector) {
    while (el && el.nodeType === 1) {
      if (el.matches(selector)) {
        return el;
      }
      el = el.parentElement;
    }
    return null;
  }

  function cssEscape(value) {
    if (window.CSS && window.CSS.escape) {
      return window.CSS.escape(value);
    }
    return String(value).replace(/([^a-zA-Z0-9_-])/g, '\\$1');
  }

  function onClick(e) {
    var toggleBtn = closest(e.target, '[data-queen-wishlist-toggle][data-handle]');
    if (toggleBtn) {
      e.preventDefault();
      var handle = toggleBtn.getAttribute('data-handle');
      var productId = toggleBtn.getAttribute('data-product-id');
      var variantId = getVariantIdForButton(toggleBtn);
      var nowActive = toggleWishlist(handle, productId, variantId);

      var allBtns = document.querySelectorAll('[data-queen-wishlist-toggle][data-handle="' + cssEscape(handle) + '"]');
      for (var i = 0; i < allBtns.length; i++) {
        setToggleState(allBtns[i], nowActive);
      }

      updateCount();
      renderWishlistPage();
      return;
    }

    var tabBtn = closest(e.target, '[data-queen-wishlist-tab]');
    if (tabBtn) {
      e.preventDefault();
      activeListId = tabBtn.getAttribute('data-list-id');
      renderWishlistPage();
      return;
    }

    var renameBtn = closest(e.target, '[data-queen-wishlist-rename-list]');
    if (renameBtn) {
      e.preventDefault();
      var renameId = renameBtn.getAttribute('data-list-id');
      var current = data.lists.filter(function (l) { return l.id === renameId; })[0];
      var newName = window.prompt(i18n.rename_list_prompt, current ? current.name : '');
      if (newName) {
        renameList(renameId, newName);
        renderWishlistPage();
      }
      return;
    }

    var deleteBtn = closest(e.target, '[data-queen-wishlist-delete-list]');
    if (deleteBtn) {
      e.preventDefault();
      var deleteId = deleteBtn.getAttribute('data-list-id');
      if (window.confirm(i18n.delete_list_confirm)) {
        deleteList(deleteId);
        if (activeListId === deleteId) {
          activeListId = DEFAULT_LIST_ID;
        }
        renderWishlistPage();
      }
      return;
    }

    var removeBtn = closest(e.target, '[data-queen-wishlist-remove]');
    if (removeBtn) {
      e.preventDefault();
      var removeId = removeBtn.getAttribute('data-item-id');
      var removedItem = data.items.filter(function (i) { return i.id === removeId; })[0];
      removeItem(removeId);
      if (removedItem) {
        var stillThere = isWishlisted(removedItem.handle);
        var btns = document.querySelectorAll('[data-queen-wishlist-toggle][data-handle="' + cssEscape(removedItem.handle) + '"]');
        for (var j = 0; j < btns.length; j++) {
          setToggleState(btns[j], stillThere);
        }
      }
      updateCount();
      renderWishlistPage();
      return;
    }

    var addCartBtn = closest(e.target, '[data-queen-wishlist-add-cart]');
    if (addCartBtn) {
      e.preventDefault();
      addToCart(addCartBtn.getAttribute('data-variant-id'), addCartBtn);
      return;
    }

    var shareBtn = closest(e.target, '[data-queen-share]');
    if (shareBtn) {
      e.preventDefault();
      var shareWrap = closest(shareBtn, '[data-share-url]');
      if (shareWrap) {
        shareProduct(
          shareBtn.getAttribute('data-queen-share'),
          shareWrap.getAttribute('data-share-url'),
          shareWrap.getAttribute('data-share-title'),
          shareBtn
        );
      }
      return;
    }
  }

  function onChange(e) {
    var moveSelect = closest(e.target, '[data-queen-wishlist-move]');
    if (moveSelect) {
      moveItem(moveSelect.getAttribute('data-item-id'), moveSelect.value);
      renderWishlistPage();
    }
  }

  function onSubmit(e) {
    var form = closest(e.target, '#queen-wishlist-create-list-form');
    if (form) {
      e.preventDefault();
      var input = document.getElementById('queen-wishlist-new-list-name');
      if (!input) {
        return;
      }
      var list = createList(input.value);
      if (list) {
        input.value = '';
        activeListId = list.id;
        renderWishlistPage();
      }
    }
  }

  /* ------------------------------------------------------------ */
  /* Init                                                           */
  /* ------------------------------------------------------------ */

  function init() {
    refreshButtonStates();
    updateCount();
    renderWishlistPage();

    document.addEventListener('click', onClick);
    document.addEventListener('change', onChange);
    document.addEventListener('submit', onSubmit);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
